import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { ABC_REFRAME_SCENARIOS } from "@/data/abcContent";
import { saveAbcExercises } from "@/lib/api";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function AbcReframeStep({ onDone, scenariosOverride }) {
  const scenarios = scenariosOverride || ABC_REFRAME_SCENARIOS;
  const [responses, setResponses] = useState({});
  const [reveal, setReveal] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const update = (id, field, value) => setResponses(r => ({ ...r, [id]: { ...(r[id]||{}), [field]: value } }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = scenarios.map(s => ({
        scenario_id: s.id,
        positive_thoughts: responses[s.id]?.positive_thoughts || "",
        positive_emotions: responses[s.id]?.positive_emotions || "",
      }));
      await saveAbcExercises(payload);
      toast.success("Práctica guardada");
      onDone(payload);
    } catch {
      toast.error("No pudimos guardar la práctica.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl sm:text-3xl font-bold text-[var(--ha-text)]">Práctica · Reformula el pensamiento</h2>
      <p className="mt-3 text-[var(--ha-muted)] leading-relaxed max-w-2xl">
        Para cada situación, verás un pensamiento automático negativo y sus emociones. Escribe una alternativa más realista
        y positiva, y las emociones que te generaría.
      </p>

      <div className="mt-8 space-y-6">
        {scenarios.map((s, i) => (
          <Card key={s.id} className="rounded-2xl border-[var(--ha-border)] bg-white">
            <CardContent className="p-6">
              <p className="text-xs uppercase tracking-[0.16em] text-[var(--ha-muted)] font-bold">Situación {i + 1}</p>
              <p className="mt-2 font-semibold text-[var(--ha-text)]">A · {s.event}</p>

              <div className="mt-4 rounded-2xl border border-[var(--ha-error)]/25 bg-[var(--ha-error)]/5 p-4">
                <p className="text-xs uppercase font-bold tracking-[0.14em] text-[var(--ha-error)] mb-2">Pensamiento automático</p>
                <ul className="text-sm text-[var(--ha-text)] space-y-1">
                  {s.negative_thoughts.map((t, ti) => <li key={ti}>• {t}</li>)}
                </ul>
                <p className="mt-3 text-xs text-[var(--ha-muted)]">Emociones: <span className="font-semibold text-[var(--ha-text)]">{s.negative_emotions.join(", ")}</span></p>
              </div>

              <div className="mt-4 grid gap-3">
                <div>
                  <label className="text-sm font-semibold text-[var(--ha-text)]">B · Tu pensamiento alternativo</label>
                  <Textarea
                    data-testid={`abc-reframe-${s.id}-thoughts`}
                    value={responses[s.id]?.positive_thoughts || ""}
                    onChange={(e) => update(s.id, "positive_thoughts", e.target.value)}
                    placeholder="¿Qué pensamiento más realista o positivo puedes elegir?"
                    className="mt-2 rounded-xl border-[var(--ha-border)] focus:border-[var(--ha-primary)] focus:ring-[var(--ha-primary)]"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-[var(--ha-text)]">C · Emociones que te generaría</label>
                  <Textarea
                    data-testid={`abc-reframe-${s.id}-emotions`}
                    value={responses[s.id]?.positive_emotions || ""}
                    onChange={(e) => update(s.id, "positive_emotions", e.target.value)}
                    placeholder="Calma, esperanza, gratitud…"
                    className="mt-2 rounded-xl border-[var(--ha-border)] focus:border-[var(--ha-primary)] focus:ring-[var(--ha-primary)]"
                  />
                </div>
              </div>

              <button
                type="button"
                data-testid={`abc-reveal-${s.id}`}
                onClick={() => setReveal({ ...reveal, [s.id]: !reveal[s.id] })}
                className="mt-4 inline-flex items-center gap-2 text-sm text-[var(--ha-primary)] hover:underline"
              >
                {reveal[s.id] ? <EyeOff className="w-4 h-4" strokeWidth={1.75} /> : <Eye className="w-4 h-4" strokeWidth={1.75} />}
                {reveal[s.id] ? "Ocultar sugerencias" : "Ver sugerencias"}
              </button>

              {reveal[s.id] && (
                <div className="mt-3 rounded-2xl border border-[var(--ha-success)]/25 bg-[var(--ha-success)]/5 p-4">
                  <p className="text-xs uppercase font-bold tracking-[0.14em] text-[var(--ha-success)] mb-2">Sugerencia</p>
                  <ul className="text-sm text-[var(--ha-text)] space-y-1">
                    {s.positive_thoughts.map((t, ti) => <li key={ti}>• {t}</li>)}
                  </ul>
                  <p className="mt-3 text-xs text-[var(--ha-muted)]">Emociones: <span className="font-semibold text-[var(--ha-text)]">{s.positive_emotions.join(", ")}</span></p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-8 flex justify-end">
        <Button
          data-testid="abc-reframe-continue-btn"
          onClick={submit}
          disabled={submitting}
          className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6"
        >
          Guardar y continuar <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
}
